import posthog from 'posthog-js'
import { z } from 'zod'
import { create } from 'zustand'

import { trackDetails, trackQuiz } from '@/services/track/track.gql'

import { queryClient } from '@/lib/tanstack-query'

import { ITaskType, TASK_TYPE } from './task-constant'
import { IQuestionItem } from './type'

type PersistValueDto = z.infer<typeof PersistValueDto>
const PersistValueDto = z.object({
  isConfirmed: z.boolean(),
  trackId: z.string(),
  quizId: z.string(),
  startTime: z.number(),
})

type Value = {
  markAsComingSoon: boolean
  duration: number
  questions: IQuestionItem[]
  questionsCount: number
  selected: IQuestionItem
  quiz: {
    title: string
  }
  countdown: number
  intervalId: NodeJS.Timeout | null
  status: 'idle' | 'running' | 'finished' | 'timeout'
  guide: string
  // @deprecated
  packages: string
}

type Action = {
  /** Setup parsed questions */
  setup: (...args: Parameters<typeof parseDataToSetup>) => Promise<void>
  setConfirmed: () => void
  onChangeQuestion: (idx: number) => void
  runCountdown: () => void
  tick: () => void
  clearCountdown: () => void
  reset: () => void
}

const initialValue: Value & PersistValueDto = {
  // Persist
  isConfirmed: false,
  trackId: '',
  quizId: '',
  startTime: 0,
  // Value
  markAsComingSoon: false,
  duration: 0,
  questions: [],
  questionsCount: 0,
  selected: null!,
  quiz: { title: '' },
  countdown: 0,
  intervalId: null,
  status: 'idle',
  guide: '',
  packages: '',
}

const useTaskQuiz = create<PersistValueDto & Action & Value>((set, get) => ({
  ...initialValue,
  // Action
  async setup(trackId, quizId) {
    const result = await parseDataToSetup(quizId, trackId)
    set({ ...result, trackId, quizId })
  },
  setConfirmed() {
    const questions = get().questions
    const selected = questions[0]!

    set({
      isConfirmed: true,
      startTime: Date.now(),
      selected,
      quiz: { title: selected.title },
      status: 'running',
    })

    get().runCountdown()
  },
  onChangeQuestion(idx) {
    const { questions, selected: oldSelected } = get()

    if (oldSelected) {
      const oldIndex = questions.findIndex((q) => q.id === oldSelected.id)
      if (oldIndex !== -1) {
        questions[oldIndex] = oldSelected
      }
    }

    const selected = questions[idx] || null!
    if (!selected) {
      const allTaskDone = questions.every((q) => q.status !== 'idle')
      if (allTaskDone) {
        get().clearCountdown()
        set({
          isConfirmed: true,
          selected,
          quiz: { title: '' },
          status: 'finished',
        })
      }

      return
    }

    set({
      questions,
      selected,
      quiz: { title: selected.title },
    })
  },
  runCountdown() {
    get().clearCountdown()

    const intervalId = setInterval(() => {
      get().tick()
    }, 1000)

    set({ intervalId })
  },
  tick() {
    const countdown = get().countdown

    if (countdown <= 1) {
      get().clearCountdown()
      set({ countdown: 0, status: 'timeout' })
      return
    }

    set({ countdown: countdown - 1 })
  },
  clearCountdown() {
    const intervalId = get().intervalId
    if (intervalId) {
      clearInterval(intervalId)
      set({ intervalId: null })
    }
  },
  reset() {
    get().clearCountdown()
    set({ ...initialValue })
  },
}))

async function parseDataToSetup(
  quizId: string,
  trackId: string
): Promise<
  Pick<
    Value,
    | 'questions'
    | 'questionsCount'
    | 'duration'
    | 'quiz'
    | 'countdown'
    | 'guide'
    | 'packages'
    | 'markAsComingSoon'
  >
> {
  const [track, quiz] = await Promise.all([
    queryClient.ensureQueryData(trackDetails.qry({ trackId, myTrack: true })),
    queryClient.ensureQueryData(trackQuiz.qry({ chapterId: quizId })),
  ])

  // Validate tutorial is mid exam
  let isMidExam = false
  for (const t of track.getListTutorialFromDetailTrack.tutorials) {
    if (!t || !t.chapters) continue
    for (const c of t.chapters) {
      if (!c) continue
      const selected = c.id === quizId
      if (!selected) continue
      isMidExam = t.mid_exam
    }
  }

  const availableTaskType = new Set(TASK_TYPE)

  const isSpeakingFeatureEnabled = posthog.isFeatureEnabled('yes_lms_speaking')
  const hasSpeaking = quiz.getQuizChapterByChapterID.quiz_chapter.some(
    (x) => x?.quiz_type === 'speaking'
  )

  const questions: IQuestionItem[] = quiz.getQuizChapterByChapterID.quiz_chapter
    .map((x, i) => {
      if (!x) return null
      const isValidTaskType = availableTaskType.has(x.quiz_type)

      if (!isValidTaskType) {
        console.warn('Unknown task type', x.quiz_type)
      }

      return {
        id: x.id,
        index: i,
        title: x.chapter.title,
        question: x.question,
        options: x.quiz_chapter_options.map((o) => ({
          id: o.id,
          option: o.option,
        })),
        explanation: '',
        answerId: [],
        status: 'idle',
        type: x.quiz_type as ITaskType,
        media: x.media,
        isMidExam,
      } satisfies IQuestionItem
    })
    .filter(Boolean)

  const chapterSample = quiz.getQuizChapterByChapterID.quiz_chapter[0]!
  const duration = chapterSample!.chapter.duration_quiz_countdown!

  return {
    questions,
    questionsCount: questions.length,
    duration,
    quiz: {
      title: chapterSample.chapter.title,
    },
    countdown: duration,
    guide: chapterSample.chapter.guide || '',
    packages: chapterSample.package!,
    markAsComingSoon: !isSpeakingFeatureEnabled && hasSpeaking,
  }
}

function formatTime(seconds: number): string {
  const hrs = Math.floor(seconds / 3600)
  const mins = Math.floor((seconds % 3600) / 60)
  const secs = Math.floor(seconds % 60)

  const pad = (val: number) => String(val).padStart(2, '0')

  return `${pad(hrs)}:${pad(mins)}:${pad(secs)}`
}

export { useTaskQuiz, formatTime }
